import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useKbPassages } from "@/hooks/use-knowledge-base";
import { failureMessage, type KnowledgeBaseFile, type PassagePage } from "@/services/knowledge-base.service";
import { formatCount } from "@/utils/kb-upload-rules";

interface PreviewDialogProps {
  /** A ready file. */
  file: KnowledgeBaseFile;
  onClose: () => void;
}

/** "Passages 21–40 of 412", or (a ready file of the old upload with none, or a page past the end) that there are none. */
function range({ page, page_size, total, passages }: PassagePage) {
  if (passages.length === 0) return "No passages on this page.";
  const from = (page - 1) * page_size + 1;
  return `Passages ${formatCount(from)}–${formatCount(from + passages.length - 1)} of ${formatCount(total)}`;
}

/**
 * What the agents read from a file (step 3 §5.6): its passages, 20 a page, each with its line breaks. Shown
 * as text, never as HTML: React escapes it, so markup in a file is shown, not run.
 */
export function PreviewDialog({ file, onClose }: PreviewDialogProps) {
  const [page, setPage] = useState(1);
  const passages = useKbPassages(file.id, page);
  const shown = passages.data;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>What your agent reads</DialogTitle>
          <DialogDescription className="truncate">{file.display_name}</DialogDescription>
        </DialogHeader>

        {passages.isPending ? (
          <div role="status" aria-label="Loading the passages" className="space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : passages.isError ? (
          <div role="alert" className="flex items-center gap-4 rounded-lg border border-dashed p-4">
            <p className="text-sm text-muted-foreground">
              {failureMessage(passages.error, "The file could not be shown. Try again in a moment.")}
            </p>
            <Button variant="outline" size="sm" onClick={() => void passages.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{range(shown)}</p>
            <ol aria-label="Passages" className="max-h-[60vh] space-y-2 overflow-y-auto">
              {shown.passages.map((passage) => (
                <li key={passage.index} className="whitespace-pre-wrap break-words rounded-md border p-3 text-sm">
                  {passage.content}
                </li>
              ))}
            </ol>
          </div>
        )}

        <div className="flex justify-between gap-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!shown || page * shown.page_size >= shown.total}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
